// Import types but defer loading the actual SDK
import type {
  SingularConfig,
  singularSdk as SingularSdkType,
} from "singular-sdk";

import { isInternalEmail } from "@medbillai/utils/email";

import { trackSingularEvent } from "./analytics";
import type {
  SingularConfigProps,
  SingularEventArgs,
  SingularEventName,
} from "./types";

// Check if we're in a browser environment
const isBrowser = typeof window !== "undefined";

// Load SDK only in browser
let singularSdk: typeof SingularSdkType | undefined;
let SingularConfigClass: typeof SingularConfig | undefined;
let sdkLoadPromise: Promise<void> | undefined;

// Type-safe module loading
if (isBrowser) {
  sdkLoadPromise = (async () => {
    try {
      const singularModule = await import("singular-sdk");

      if (
        singularModule &&
        "singularSdk" in singularModule &&
        "SingularConfig" in singularModule
      ) {
        singularSdk = singularModule.singularSdk;
        SingularConfigClass = singularModule.SingularConfig;
      } else {
        console.error(
          "[SINGULAR]: Invalid module format - missing required singular-sdk components",
        );
      }
    } catch (error) {
      console.error("[SINGULAR]: Failed to load SDK:", error);
    }
  })();
}

type PendingEvent = {
  eventName: SingularEventName;
  args?: SingularEventArgs;
};

type InitializationState =
  | { status: "pending"; events: PendingEvent[] }
  | { status: "disabled" }
  | { status: "initialized" };

class SingularService {
  private initializationState: InitializationState = {
    status: "pending",
    events: [],
  };
  private userId: string | undefined = undefined;
  private userEmail: string | undefined = undefined;

  async initialize(config: SingularConfigProps): Promise<void> {
    // Early return if not in browser or tracking disabled
    if (!isBrowser || config.singularTrackingEnabled === false) {
      console.log(
        "[SINGULAR]: Skipping initialization (not in browser or tracking disabled)",
      );
      this.completeInitialization(false);
      return;
    }

    if (this.initializationState.status === "initialized") {
      console.warn("[SINGULAR]: Already initialized");
      return;
    }

    // Wait for SDK to load if it hasn't already
    if (sdkLoadPromise) {
      console.log("[SINGULAR]: Waiting for SDK to load...");
      try {
        await sdkLoadPromise;
      } catch (error) {
        console.error(
          "[SINGULAR]: Error while waiting for SDK to load:",
          error,
        );
        this.completeInitialization(false);
        return;
      }
    }

    // Check if SDK loaded properly
    if (!singularSdk || !SingularConfigClass) {
      console.error(
        "[SINGULAR]: SDK not loaded properly - missing SDK components",
      );
      this.completeInitialization(false);
      return;
    }

    const singularConfig = new SingularConfigClass(
      config.singularSdkKey,
      config.singularSdkSecret,
      "com.granted.app",
    );
    // Set session timeout equivalent to the native 180 seconds
    singularConfig.withSessionTimeoutInMinutes(3);
    singularConfig.withAutoPersistentSingularDeviceId("grantedhealth.com");

    if (this.userId !== undefined) {
      singularConfig.withCustomUserId(this.userId);
    }

    try {
      singularSdk.init(singularConfig);
    } catch (error) {
      console.error("[SINGULAR]: Failed to initialize SDK:", error);
      this.completeInitialization(false);
      return;
    }
    this.completeInitialization(true);
  }

  private completeInitialization(singularTrackingEnabled: boolean) {
    const pendingEvents =
      this.initializationState.status === "pending"
        ? this.initializationState.events
        : [];
    this.initializationState = {
      status: singularTrackingEnabled ? "initialized" : "disabled",
    };
    for (const { eventName, args } of pendingEvents) {
      this.trackAndDeliverEvent(eventName, args);
    }
  }

  setUser({ id, email }: { id: string; email?: string }) {
    this.userId = id;

    if (email) {
      this.userEmail = email;
    }

    if (!isBrowser || !singularSdk) {
      return;
    }

    singularSdk.login(id);
  }

  resetUser() {
    this.userId = undefined;
    this.userEmail = undefined;

    if (!isBrowser || !singularSdk) {
      return;
    }

    if (this.initializationState.status === "initialized") {
      singularSdk.logout();
    }
  }

  event(eventName: SingularEventName, args?: SingularEventArgs) {
    // SSR never initializes the browser SDK, so its events must not be queued.
    if (isBrowser && this.initializationState.status === "pending") {
      this.initializationState.events.push({ eventName, args });
      return;
    }

    this.trackAndDeliverEvent(eventName, args);
  }

  private trackAndDeliverEvent(
    eventName: SingularEventName,
    args?: SingularEventArgs,
  ) {
    const skipForInternalUser = isInternalEmail(this.userEmail) === true;
    trackSingularEvent(eventName, skipForInternalUser, args);

    if (skipForInternalUser) {
      console.log("[SINGULAR]: Skipping event for internal user");
      return;
    }

    if (this.initializationState.status !== "initialized" || !singularSdk) {
      return;
    }

    if (args) {
      singularSdk.event(eventName, args);
    } else {
      singularSdk.event(eventName);
    }
  }
}

const singularService = new SingularService();

export { singularService };
